import { useState } from 'react';
import { Link } from 'react-router-dom';
import './SupportPage.css';

const API_URL = 'https://api-lexira.mindgrid.fun/v1/public/feedback';

const TYPE_OPTIONS = [
  { value: 'gameplay_issue', label: '🎮 Gameplay Issue' },
  { value: 'bug_report',     label: '🐛 Bug Report'     },
  { value: 'suggestion',     label: '💡 Suggestion'     },
  { value: 'other',          label: '📩 Other'           },
];

const INITIAL = { name: '', email: '', type: '', message: '' };

function Field({ label, error, children }) {
  return (
    <div className={`sf-field ${error ? 'has-error' : ''}`}>
      <label className="sf-label">{label}</label>
      {children}
      {error && <span className="sf-error">{error}</span>}
    </div>
  );
}

function validate(data) {
  const e = {};
  if (!data.name.trim())                     e.name    = 'Name is required.';
  if (!data.email.trim())                    e.email   = 'Email is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
                                             e.email   = 'Enter a valid email.';
  if (!data.type)                            e.type    = 'Please select a type.';
  if (!data.message.trim())                  e.message = 'Message is required.';
  else if (data.message.trim().length < 10)  e.message = 'Message must be at least 10 characters.';
  return e;
}

export default function SupportPage() {
  const [form, setForm]         = useState(INITIAL);
  const [errors, setErrors]     = useState({});
  const [status, setStatus]     = useState('idle');
  const [apiError, setApiError] = useState('');
  const [ticketId, setTicketId] = useState(null);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: '' }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus('loading');
    setApiError('');

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:    form.name.trim(),
          email:   form.email.trim(),
          type:    form.type,
          message: form.message.trim(),
        }),
      });

      if (res.status === 429) {
        setApiError('Too many requests — please wait a minute and try again.');
        setStatus('error');
        return;
      }
      if (!res.ok) {
        setApiError(`Something went wrong (${res.status}). Please try again.`);
        setStatus('error');
        return;
      }

      const data = await res.json();
      setTicketId(data.id);
      setStatus('success');
      setForm(INITIAL);
    } catch {
      setApiError('Could not reach the server. Check your connection and try again.');
      setStatus('error');
    }
  }

  return (
    <div className="support-page">
      <div className="support-wrap">

        {/* Back link */}
        <Link to="/" className="legal-back">← Back to Home</Link>

        {/* Header */}
        <div className="support-header">
          <img src="/assets/mascot/mascot-love.svg" alt="Lexi mascot" className="support-mascot" />
          <div>
            <h1 className="support-title">Support &amp; Feedback</h1>
            <p className="support-subtitle">
              Found a bug? Have a suggestion? We read every message and reply within 48 hours.
            </p>
          </div>
        </div>

        {/* Card */}
        <div className="support-card">
          {status === 'success' ? (
            <div className="sf-success">
              <span className="sf-success-icon">✅</span>
              <h2>Message received!</h2>
              <p>
                Ticket <strong>#{ticketId}</strong> created. We'll reply within 48 hours.
              </p>
              <button className="sf-btn" onClick={() => { setStatus('idle'); setTicketId(null); }}>
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>

              <div className="sf-row">
                <Field label="Name" error={errors.name}>
                  <input className="sf-input" type="text" name="name"
                    value={form.name} onChange={handleChange}
                    placeholder="Your name" autoComplete="name" />
                </Field>
                <Field label="Email" error={errors.email}>
                  <input className="sf-input" type="email" name="email"
                    value={form.email} onChange={handleChange}
                    placeholder="you@example.com" autoComplete="email" />
                </Field>
              </div>

              <Field label="Type" error={errors.type}>
                <select className="sf-select" name="type" value={form.type} onChange={handleChange}>
                  <option value="">Select a category…</option>
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Message" error={errors.message}>
                <textarea className="sf-textarea" name="message"
                  value={form.message} onChange={handleChange}
                  placeholder="Describe your issue or idea…" rows={5} />
              </Field>

              {status === 'error' && (
                <div className="sf-api-error">⚠️ {apiError}</div>
              )}

              <button className="sf-btn" type="submit" disabled={status === 'loading'}>
                {status === 'loading' ? <span className="sf-spinner" /> : 'Send Message →'}
              </button>

            </form>
          )}
        </div>

        {/* Subtle footer note */}
        <p className="support-note">
          Or email us directly at{' '}
          <a href="mailto:support@lexira.app">support@lexira.app</a>
        </p>

      </div>
    </div>
  );
}
